export const positioning = {
  eyebrow: 'About',
  title: 'We are the studio you hire when the website has to earn its price.',
  lede:
    'ShreyasStudio is a small independent design and build practice in India. We work with founder-led companies, one project at a time, and we take responsibility for the outcome rather than the deliverable.',
}

export const story: { heading: string; paragraphs: string[] } = {
  heading: 'Why this studio exists',
  paragraphs: [
    'Agency work has a way of getting worse on its way to launch. A project starts with a strategy deck nobody reads, passes through several account layers, and arrives at a design team who have never spoken to the client. By launch, the site is a compromise between eleven opinions and one deadline, and the client has paid for the process rather than the result.',
    'This studio is built the opposite way. It is deliberately small, takes one project at a time, and has no account layer. The person who understands your business is the person drawing the screens and writing the code. Nothing gets translated, so nothing gets lost.',
    'We believe design is a commercial instrument, not decoration. A website has a job — sell a product, book a call, make a serious company look serious — and every decision on it should be defensible against that job. If we cannot explain why an element exists, it should not exist.',
    'We also believe most small companies are badly served by the way this industry sells. Hourly billing rewards slowness. Open-ended timelines reward indecision. Retainers get sold before anyone knows whether they are needed. We quote a fixed price against a fixed date, and we absorb the risk of our own estimates.',
  ],
}

export const principles: { title: string; description: string }[] = [
  {
    title: 'Strategy before pixels',
    description:
      'No layout gets drawn until we can state, in one sentence, what the site is for and who it is talking to. Design without that sentence is just taste.',
  },
  {
    title: 'Ship in weeks, not quarters',
    description:
      'Momentum is a design tool. Long projects accumulate opinions rather than quality, so we work in three to five week engagements with a launch date agreed on day one.',
  },
  {
    title: 'One number decides everything',
    description:
      'Every project gets a single measure of success before it starts — enquiries, conversion, revenue, time saved. Decisions get made against that number, not against preference.',
  },
  {
    title: 'Fewer, better decisions',
    description:
      'We remove more than we add. A site with five deliberate elements outperforms one with twenty, and it is far easier for your team to maintain after we leave.',
  },
  {
    title: 'Say the difficult thing early',
    description:
      'If your positioning is unclear or your idea is wrong for the goal, you will hear it in week one. That is the part you are actually paying for.',
  },
  {
    title: 'Leave it maintainable',
    description:
      'Every project ends with a CMS your team can run and a recorded walkthrough. We would rather you did not need us for content changes.',
  },
]

export const operatingModel: { label: string; value: string; detail: string }[] = [
  {
    label: 'Engagement shape',
    value: 'One project at a time',
    detail:
      'We take a single client through a project from kickoff to launch. Nothing is queued behind other work.',
  },
  {
    label: 'Communication',
    value: 'One shared channel',
    detail:
      'A single Slack or email thread with the people doing the work in it. No account manager, no relay, no status meetings that exist to fill a calendar.',
  },
  {
    label: 'Cadence',
    value: 'Weekly review, Thursdays',
    detail:
      'A 30-minute call each Thursday with work in front of you, plus a written Monday note covering what shipped, what is next and what we need from you.',
  },
  {
    label: 'Response time',
    value: 'Within one working day',
    detail:
      'Every message gets a real answer inside one working day, usually the same morning. Urgent means a phone call, and that is fine.',
  },
  {
    label: 'Reviews and revisions',
    value: 'Consolidated rounds',
    detail:
      'Feedback comes in one collected pass per round rather than a stream of separate notes. The number of rounds is agreed in the Scope of Work before the project starts.',
  },
  {
    label: 'Working hours',
    value: '10:00–19:00 IST, Mon–Fri',
    detail:
      'We keep normal working hours and we do not pretend to be available at midnight. Deadlines still hold.',
  },
]

export const studioImage: { image: string; alt: string; caption: string } = {
  image:
    'https://cdn.magicpatterns.com/patterns/generated-images/0a952b50-bcdc-41eb-b9cc-3c648598edbf.jpg',
  alt: 'The studio desk: a closed laptop, printed layout proofs, an open notebook and a pinboard of paper swatches',
  caption: 'The studio, mid-project',
}

export const coreTeam: { title: string; description: string; roles: string[] } = {
  title: 'A small independent studio with a trusted network of specialists',
  description:
    'The studio is deliberately small, and it stays that way on purpose: the person who understands your business is the person drawing the screens and writing the code. When a project genuinely needs a specialist — illustration, motion, technical SEO, photography — we bring in someone we have worked with before, briefed and managed by us. You never manage a freelancer, and no project is staffed with people we do not know. It is a smaller model than an agency, and it is why the work stays consistent.',
  roles: [
    'Illustration and iconography',
    'Motion design',
    'Technical SEO',
    'Photography and retouching',
    'Copywriting',
    'Accessibility audit',
  ],
}

export const workingApproach: { title: string; description: string }[] = [
  {
    title: 'Design and strategy',
    description:
      'Positioning, structure, art direction and the design of every screen. This is where the difficult questions about the brief get asked.',
  },
  {
    title: 'Build and performance',
    description:
      'The design taken into Webflow, Framer, WordPress or Next.js, with performance, accessibility and the launch itself owned end to end.',
  },
]

export const capabilityGroups: { title: string; items: string[] }[] = [
  {
    title: 'Design',
    items: ['Brand strategy', 'Identity systems', 'Web design', 'Product and app UI', 'Design systems', 'Art direction'],
  },
  {
    title: 'Build',
    items: ['Webflow', 'Framer', 'Next.js', 'Shopify', 'WordPress', 'CMS setup and training'],
  },
  {
    title: 'Craft and quality',
    items: ['Accessibility to WCAG AA', 'Core Web Vitals', 'Technical SEO foundation', 'Cross-browser QA', 'Analytics setup', 'Motion and interaction'],
  },
]

export const toolset: { name: string; use: string }[] = [
  { name: 'Figma', use: 'Design, prototypes and handoff' },
  { name: 'Slack', use: 'The single shared channel' },
  { name: 'Linear', use: 'Project tracking you can see' },
  { name: 'Loom', use: 'Recorded walkthroughs and reviews' },
  { name: 'Notion', use: 'Briefs, decisions and content' },
  { name: 'Plausible', use: 'Analytics without the bloat' },
]
